import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import classNames from 'classnames';
import { DropzoneArea } from 'mui-file-dropzone';
import { Alert, Button, FormControl, Grid, Link } from '@mui/material';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import PreferencesService from 'services/api/PreferencesService';
import Table from 'components/shared/Table';
import { TableColumn } from 'store/types/Table';
import FileInfo from 'store/types/FileInfo';
import { PreferenceQuestionView } from 'store/types/AuraAttribute';
import { FormProps, getValidationProps } from 'util/Form';
import { getFormattedFileSize } from 'util/Format';
import { defaultGridContainerProps, defaultGridItemProps, defaultSnackbarErrorProps } from 'util/Layout';

import commonStyles from 'styles/common.module.scss';
import styles from './FileQuestionFormItem.module.scss';

interface FileQuestionFormItemProps extends FormProps {
  question: PreferenceQuestionView;
  showTable?: boolean;
}

const MAX_FILE_SIZE = 5000000;
const MAX_FILES_COUNT = 5;

const FileQuestionFormItem: React.FunctionComponent<FileQuestionFormItemProps> = ({
  question,
  disabled,
  showTable = true,
}) => {
  const { fieldName, required = false, answers } = useMemo(() => question, [question]);
  const {
    control,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = useFormContext();
  const [list, setList] = useState<FileInfo[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState<boolean>();
  const skipFirst = React.useRef(true);
  const answerFiles = useWatch({ control, name: fieldName });
  const { error } = getValidationProps(`files.${fieldName}`, errors);

  useEffect(() => {
    const files = answers?.flatMap((answer) => answer.files ?? []) ?? [];
    if (files?.length) {
      setValue(fieldName, files);
      setList(files);
    }
  }, [answers, fieldName, setValue, trigger]);

  const handleDeleteButtonClick = useCallback(
    (record: FileInfo) => () => {
      const value = getValues()[fieldName];
      const newValue = value.filter((file: FileInfo) => file.id !== record.id);

      setValue(fieldName, newValue);
      setList(newValue);
      void trigger(fieldName);
    },
    [fieldName, getValues, setValue, trigger]
  );

  const handleFileLinkClick = useCallback(
    (record: FileInfo) => () => {
      setLoading(true);
      PreferencesService.getAttributeFile(record)
        .then(() => {
          setLoading(false);
        })
        .catch((error: string) => {
          enqueueSnackbar(error, defaultSnackbarErrorProps);
          setLoading(false);
        });
    },
    [enqueueSnackbar]
  );

  const columns: Array<TableColumn<FileInfo>> = useMemo(() => {
    if (!showTable) return [];

    const result: Array<TableColumn<FileInfo>> = [
      {
        dataIndex: 'name',
        label: 'File Name',
        render: (name: string, record: FileInfo) => (
          <Link onClick={handleFileLinkClick(record)} className={commonStyles.link}>
            {name}
          </Link>
        ),
      },
      {
        dataIndex: 'size',
        label: 'Size',
        render: (size: number) => getFormattedFileSize(size),
      },
    ];

    !disabled &&
      result.push({
        label: 'Action',
        render: (_, record) => (
          <Button
            size={'small'}
            color={'primary'}
            variant={'outlined'}
            className={commonStyles.dangerButtonOutlined}
            onClick={handleDeleteButtonClick(record)}
          >
            <DeleteOutlined className={styles.icon} />
          </Button>
        ),
      });

    return result;
  }, [disabled, handleDeleteButtonClick, handleFileLinkClick, showTable]);

  const validateFilesField = useCallback(
    (value: File[]) => {
      return (required || required) && !answerFiles?.length && !list?.length ? !!value?.length : true;
    },
    [answerFiles?.length, list?.length, required]
  );

  return (
    <Grid {...defaultGridContainerProps}>
      <Grid {...defaultGridItemProps} lg={showTable ? 6 : 12}>
        <FormControl required={required} error={error} className={styles.formControl}>
          <Controller
            name={`files.${fieldName}`}
            control={control}
            defaultValue={[]}
            rules={{
              validate: validateFilesField,
            }}
            render={({ field }) => (
              <DropzoneArea
                maxFileSize={MAX_FILE_SIZE}
                filesLimit={MAX_FILES_COUNT}
                fileObjects={field.value ?? []}
                onChange={(newFileObjects) => {
                  if (skipFirst.current) {
                    skipFirst.current = false;
                    return;
                  }
                  field.onChange(newFileObjects);
                }}
                onDelete={field.onChange}
                showAlerts={['error']}
                clearOnUnmount={true}
                showPreviews
                showPreviewsInDropzone={false}
                useChipsForPreview
                previewText=""
                classes={{
                  root: classNames(styles.dropzone, { [styles.disabled]: disabled }),
                  textContainer: styles.dropzoneTextContainer,
                  text: styles.dropzoneText,
                  icon: styles.dropzoneIcon,
                }}
                previewGridClasses={{
                  container: styles.previewContainer,
                  item: styles.previewImageContainer,
                }}
                dropzoneProps={{ disabled, noDrag: false }}
                previewChipProps={{ className: styles.chip, disabled }}
                alertSnackbarProps={{
                  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                }}
              />
            )}
          />
        </FormControl>
      </Grid>

      {showTable && (
        <Grid {...defaultGridItemProps} lg={6}>
          <Table columns={columns} list={list} className={styles.sectionTable} loading={loading} />
        </Grid>
      )}

      <Grid {...defaultGridItemProps}>
        <Alert severity="error" className={commonStyles.alert} style={{ marginTop: '1rem' }} hidden={!error}>
          Please upload a file.
        </Alert>
      </Grid>
    </Grid>
  );
};

export default FileQuestionFormItem;
